import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface MensagemAjuda {
    id: number;
    id_usuario: number | null;
    id_banda: number | null;
    id_empresa: number | null;
    nome: string;
    email: string | null;
    telefone: string | null;
    mensagem: string;
    tela: string;
    data_envio: string;
    nome_usuario?: string;
    nome_banda?: string;
    nome_empresa?: string;
}

const MensagensAjuda: React.FC = () => {
    const navigate = useNavigate();
    const [mensagens, setMensagens] = useState<MensagemAjuda[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMensagem, setSelectedMensagem] = useState<MensagemAjuda | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filterTela, setFilterTela] = useState<string>('all');

    useEffect(() => {
        fetchMensagens();
    }, []);

    const fetchMensagens = async () => {
        setLoading(true);
        setError(null);
        try {
            console.log('🔄 Buscando mensagens de ajuda...');
            const response = await api.get('/admin/ajuda/listar');

            console.log('📦 Mensagens recebidas:', response.data);
            setMensagens(response.data);
        } catch (err) {
            console.error('❌ Erro ao carregar mensagens:', err);
            setError('Erro ao carregar mensagens de ajuda.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('pt-BR');
    };

    const getRemetente = (mensagem: MensagemAjuda): string => {
        if (mensagem.nome_usuario) return `👤 ${mensagem.nome_usuario}`;
        if (mensagem.nome_banda) return `🎸 ${mensagem.nome_banda}`;
        if (mensagem.nome_empresa) return `🏢 ${mensagem.nome_empresa}`;
        return `📝 ${mensagem.nome}`;
    };

    const getTipoRemetente = (mensagem: MensagemAjuda): string => {
        if (mensagem.id_usuario) return 'Usuário';
        if (mensagem.id_banda) return 'Banda';
        if (mensagem.id_empresa) return 'Empresa';
        return 'Visitante';
    };

    const openModal = (mensagem: MensagemAjuda) => {
        setSelectedMensagem(mensagem);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setSelectedMensagem(null);
        setIsModalOpen(false);
    };

    // Filtrar mensagens por busca e tela
    const filterMensagens = () => {
        let filtered = mensagens;

        // Filtro por tela
        if (filterTela !== 'all') {
            filtered = filtered.filter(m => m.tela === filterTela);
        }

        // Filtro por busca
        if (searchTerm) {
            filtered = filtered.filter(m =>
                m.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                m.mensagem.toLowerCase().includes(searchTerm.toLowerCase()) ||
                m.tela.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (m.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (m.telefone?.includes(searchTerm))
            );
        }

        return filtered;
    };

    // Obter lista única de telas para filtro
    const getTelasUnicas = () => {
        const telasSet = new Set(mensagens.map(m => m.tela));
        const telas = Array.from(telasSet);
        return telas.sort();
    };

    const mensagensFiltradas = filterMensagens();

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <button onClick={() => navigate('/admin')} style={styles.backButton}>
                    ← Voltar ao Painel
                </button>
                <h1 style={styles.title}>Mensagens de Ajuda</h1>
            </div>

            {/* Filtros */}
            <div style={styles.filtersContainer}>
                <input
                    type="text"
                    placeholder="Buscar por nome, email, telefone ou mensagem..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                />

                <select
                    value={filterTela}
                    onChange={(e) => setFilterTela(e.target.value)}
                    style={styles.selectFilter}
                >
                    <option value="all">Todas as Telas</option>
                    {getTelasUnicas().map(tela => (
                        <option key={tela} value={tela}>{tela}</option>
                    ))}
                </select>
            </div>

            {/* Estatísticas */}
            <div style={styles.statsContainer}>
                <div style={styles.statCard}>
                    <span style={styles.statNumber}>{mensagens.length}</span>
                    <span style={styles.statLabel}>Total de Mensagens</span>
                </div>
                <div style={styles.statCard}>
                    <span style={styles.statNumber}>{mensagensFiltradas.length}</span>
                    <span style={styles.statLabel}>Mensagens Filtradas</span>
                </div>
                <div style={styles.statCard}>
                    <span style={styles.statNumber}>{getTelasUnicas().length}</span>
                    <span style={styles.statLabel}>Telas Distintas</span>
                </div>
            </div>

            {loading && <p style={styles.message}>Carregando...</p>}
            {error && <p style={{ ...styles.message, color: '#dc3545' }}>{error}</p>}

            {!loading && (
                <div style={styles.mensagensList}>
                    {mensagensFiltradas.map((mensagem) => (
                        <div key={mensagem.id} style={styles.mensagemCard}>
                            <div style={styles.mensagemInfo}>
                                <div style={styles.mensagemHeader}>
                                    <span style={styles.mensagemId}>#{mensagem.id}</span>
                                    <span style={styles.mensagemDate}>
                                        {formatDate(mensagem.data_envio)}
                                    </span>
                                </div>

                                <div style={styles.mensagemContent}>
                                    <p style={styles.mensagemRow}>
                                        <strong>De:</strong> {getRemetente(mensagem)}
                                        <span style={styles.tipoRemetente}>({getTipoRemetente(mensagem)})</span>
                                    </p>

                                    <p style={styles.mensagemRow}>
                                        <strong>Tela:</strong> {mensagem.tela}
                                    </p>

                                    {/* Informações de contato */}
                                    {(mensagem.email || mensagem.telefone) && (
                                        <div style={styles.contatoContainer}>
                                            {mensagem.email && (
                                                <p style={styles.contatoRow}>
                                                    <strong>📧 Email:</strong>
                                                    <a href={`mailto:${mensagem.email}`} style={styles.contatoLink}>
                                                        {mensagem.email}
                                                    </a>
                                                </p>
                                            )}
                                            {mensagem.telefone && (
                                                <p style={styles.contatoRow}>
                                                    <strong>📱 WhatsApp:</strong>
                                                    <a
                                                        href={`https://wa.me/${mensagem.telefone.replace(/\D/g, '')}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={styles.contatoLink}
                                                    >
                                                        {mensagem.telefone}
                                                    </a>
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    <p style={styles.mensagemRow}>
                                        <strong>Mensagem:</strong> {mensagem.mensagem.substring(0, 100)}
                                        {mensagem.mensagem.length > 100 && '...'}
                                    </p>
                                </div>
                            </div>

                            <div style={styles.actions}>
                                <button
                                    style={styles.detailButton}
                                    onClick={() => openModal(mensagem)}
                                >
                                    Ver Detalhes
                                </button>
                            </div>
                        </div>
                    ))}
                    {mensagensFiltradas.length === 0 && (
                        <p style={styles.message}>
                            {searchTerm || filterTela !== 'all'
                                ? 'Nenhuma mensagem encontrada com os filtros aplicados.'
                                : 'Nenhuma mensagem de ajuda encontrada.'}
                        </p>
                    )}
                </div>
            )}

            {/* Modal de Detalhes */}
            {isModalOpen && selectedMensagem && (
                <div style={styles.modalOverlay} onClick={closeModal}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>Mensagem de Ajuda #{selectedMensagem.id}</h2>
                            <button onClick={closeModal} style={styles.closeButton}>
                                ✕
                            </button>
                        </div>

                        <div style={styles.modalContent}>
                            <div style={styles.modalSection}>
                                <div style={styles.modalDate}>
                                    Recebida em: {formatDate(selectedMensagem.data_envio)}
                                </div>
                                <div style={styles.telaBadge}>
                                    Tela: {selectedMensagem.tela}
                                </div>
                            </div>

                            <div style={styles.modalSection}>
                                <h3 style={styles.sectionTitle}>Informações do Remetente</h3>
                                <p><strong>Nome:</strong> {selectedMensagem.nome}</p>
                                <p><strong>Tipo:</strong> {getTipoRemetente(selectedMensagem)}</p>
                                {selectedMensagem.id_usuario && <p><strong>ID Usuário:</strong> {selectedMensagem.id_usuario}</p>}
                                {selectedMensagem.id_banda && <p><strong>ID Banda:</strong> {selectedMensagem.id_banda}</p>}
                                {selectedMensagem.id_empresa && <p><strong>ID Empresa:</strong> {selectedMensagem.id_empresa}</p>}
                                {selectedMensagem.nome_usuario && <p><strong>Nome Cadastrado:</strong> {selectedMensagem.nome_usuario}</p>}
                                {selectedMensagem.nome_banda && <p><strong>Nome Cadastrado:</strong> {selectedMensagem.nome_banda}</p>}
                                {selectedMensagem.nome_empresa && <p><strong>Nome Cadastrado:</strong> {selectedMensagem.nome_empresa}</p>}

                                {/* Informações de contato no modal */}
                                {(selectedMensagem.email || selectedMensagem.telefone) && (
                                    <div style={styles.contatoModalContainer}>
                                        <h4 style={styles.contatoModalTitle}>📞 Contatos:</h4>
                                        {selectedMensagem.email && (
                                            <p style={styles.contatoModalRow}>
                                                <strong>📧 Email:</strong>
                                                <a href={`mailto:${selectedMensagem.email}`} style={styles.contatoLink}>
                                                    {selectedMensagem.email}
                                                </a>
                                            </p>
                                        )}
                                        {selectedMensagem.telefone && (
                                            <p style={styles.contatoModalRow}>
                                                <strong>📱 WhatsApp:</strong>
                                                <a
                                                    href={`https://wa.me/${selectedMensagem.telefone.replace(/\D/g, '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={styles.contatoLink}
                                                >
                                                    {selectedMensagem.telefone}
                                                </a>
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div style={styles.modalSection}>
                                <h3 style={styles.sectionTitle}>Mensagem</h3>
                                <div style={styles.mensagemBox}>
                                    <p style={styles.mensagemText}>{selectedMensagem.mensagem}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
        backgroundColor: '#f9f9f9',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '30px',
        gap: '20px',
    },
    backButton: {
        padding: '10px 15px',
        backgroundColor: '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '14px',
    },
    title: {
        fontSize: '24px',
        color: '#333',
        margin: 0,
        fontWeight: 'bold',
    },
    filtersContainer: {
        display: 'flex',
        gap: '15px',
        marginBottom: '20px',
        flexWrap: 'wrap' as 'wrap',
    },
    searchInput: {
        flex: '1',
        minWidth: '200px',
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '16px',
        boxSizing: 'border-box' as 'border-box',
    },
    selectFilter: {
        padding: '12px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '16px',
        backgroundColor: 'white',
        cursor: 'pointer',
        minWidth: '200px',
    },
    statsContainer: {
        display: 'flex',
        gap: '20px',
        marginBottom: '30px',
        flexWrap: 'wrap' as 'wrap',
    },
    statCard: {
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        textAlign: 'center' as 'center',
        minWidth: '150px',
        display: 'flex',
        flexDirection: 'column' as 'column',
        gap: '5px',
    },
    statNumber: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#ff6600',
    },
    statLabel: {
        fontSize: '14px',
        color: '#666',
    },
    message: {
        fontSize: '16px',
        textAlign: 'center' as 'center',
        color: '#555',
        padding: '20px',
    },
    mensagensList: {
        display: 'flex',
        flexDirection: 'column' as 'column',
        gap: '15px',
    },
    mensagemCard: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    mensagemInfo: {
        flex: '1',
        marginRight: '20px',
    },
    mensagemHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px',
        paddingBottom: '10px',
        borderBottom: '2px solid #ff6600',
    },
    mensagemId: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#ff6600',
    },
    mensagemDate: {
        fontSize: '13px',
        color: '#666',
    },
    mensagemContent: {
        display: 'flex',
        flexDirection: 'column' as 'column',
        gap: '8px',
    },
    mensagemRow: {
        fontSize: '14px',
        lineHeight: '1.5',
        margin: '3px 0',
    },
    tipoRemetente: {
        color: '#666',
        fontSize: '12px',
        marginLeft: '5px',
        fontStyle: 'italic',
    },
    contatoContainer: {
        marginTop: '10px',
        padding: '10px',
        backgroundColor: '#e8f5e8',
        borderRadius: '4px',
        borderLeft: '3px solid #28a745',
    },
    contatoRow: {
        fontSize: '13px',
        margin: '3px 0',
        lineHeight: '1.4',
    },
    contatoLink: {
        color: '#007bff',
        textDecoration: 'none',
        marginLeft: '5px',
        fontWeight: 'bold',
    },
    actions: {
        display: 'flex',
        flexDirection: 'column' as 'column',
        gap: '10px',
        flexShrink: 0,
    },
    detailButton: {
        padding: '10px 15px',
        backgroundColor: '#ff6600',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
    },
    // Modal styles
    modalOverlay: {
        position: 'fixed' as 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
    },
    modal: {
        backgroundColor: 'white',
        borderRadius: '8px',
        maxWidth: '700px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px',
        borderBottom: '2px solid #ff6600',
        backgroundColor: '#fff7f0',
    },
    modalTitle: {
        margin: 0,
        fontSize: '20px',
        color: '#333',
        fontWeight: 'bold',
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '24px',
        cursor: 'pointer',
        color: '#666',
        padding: '5px',
        lineHeight: '1',
    },
    modalContent: {
        padding: '20px',
    },
    modalSection: {
        marginBottom: '20px',
        padding: '15px',
        border: '1px solid #e9ecef',
        borderRadius: '4px',
        backgroundColor: '#fafafa',
    },
    telaBadge: {
        padding: '8px 12px',
        backgroundColor: '#ff6600',
        color: 'white',
        borderRadius: '12px',
        fontSize: '14px',
        fontWeight: 'bold',
        display: 'inline-block',
        marginTop: '10px',
    },
    modalDate: {
        fontSize: '14px',
        color: '#666',
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333',
        marginBottom: '12px',
        marginTop: 0,
        borderBottom: '1px solid #ddd',
        paddingBottom: '5px',
    },
    contatoModalContainer: {
        marginTop: '15px',
        padding: '12px',
        backgroundColor: '#f0f8ff',
        borderRadius: '6px',
        border: '1px solid #007bff',
    },
    contatoModalTitle: {
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#333',
        marginBottom: '8px',
        marginTop: 0,
    },
    contatoModalRow: {
        fontSize: '13px',
        margin: '5px 0',
        lineHeight: '1.4',
    },
    mensagemBox: {
        padding: '15px',
        backgroundColor: '#fff',
        borderRadius: '4px',
        border: '1px solid #ddd',
        fontSize: '14px',
    },
    mensagemText: {
        margin: 0,
        lineHeight: '1.6',
        whiteSpace: 'pre-wrap' as 'pre-wrap',
    },
};

export default MensagensAjuda;
